import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import GuideDialog from "@/components/GuideDialog";
import { INSIGHTS } from "@/data/insights";
import { useSEO, breadcrumb, SITE } from "@/lib/useSEO";

const [featured, ...rest] = INSIGHTS;
const DOWNLOADS = INSIGHTS.filter((a) => a.pdf);

const INSIGHTS_ITEMLIST = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "My Floor Studio — Journal",
  description:
    "Specification guides and material insights from My Floor Studio, Drummoyne — timber, carpet, tile and stone flooring for Sydney architects, designers and homeowners.",
  numberOfItems: INSIGHTS.length,
  itemListElement: INSIGHTS.map((a, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${SITE}/insights/${a.slug}`,
    name: a.title,
  })),
};

function ArticleMeta({ article }) {
  return (
    <span className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">
      {article.category} · {article.readMins} min read
    </span>
  );
}

export default function Insights() {
  const [guide, setGuide] = useState(null);
  useSEO({
    title: "Journal · Flooring Specification Guides — My Floor Studio",
    description:
      "Specification guides and material insights from My Floor Studio: element guides to timber, carpet, tile and stone, choosing floors room by room, engineered vs solid timber, and AS 4586 slip ratings for Australian homes.",
    path: "/insights",
    keywords:
      "flooring guides, flooring specification, engineered vs solid timber, slip ratings AS 4586, best flooring for each room, Sydney flooring advice, interior design flooring insights",
    imageAlt: "My Floor Studio Journal — flooring specification guides",
    jsonLd: [
      INSIGHTS_ITEMLIST,
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Journal", path: "/insights" },
      ]),
    ],
  });

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Nav />
      <main role="main" className="pt-32 md:pt-40">
        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-14">
          <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-8">Journal</p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl sm:text-6xl md:text-7xl xl:text-8xl leading-[0.95] max-w-3xl">
              Notes on floors
            </h1>
            <p className="text-[#D6D1CA]/70 font-body max-w-sm text-sm md:text-base">
              Specification guides and material thinking from the studio — written to help
              architects, designers and homeowners choose floors that hold their beauty for decades.
            </p>
          </div>
        </section>

        {/* Featured article */}
        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-16">
          <Link
            to={`/insights/${featured.slug}`}
            data-testid={`insight-featured-${featured.slug}`}
            className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="aspect-[16/10] overflow-hidden rounded-sm bg-[#0f1012]"
            >
              <img
                src={featured.heroImage}
                alt={featured.heroAlt}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                fetchPriority="high"
              />
            </motion.div>
            <div>
              <ArticleMeta article={featured} />
              <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-4xl md:text-5xl leading-[1.02] mt-4">
                {featured.title}
              </h2>
              <p className="text-[#D6D1CA]/70 text-lg font-body mt-3">{featured.subtitle}</p>
              <p className="text-[#D6D1CA]/70 text-[15px] font-body leading-[1.7] mt-6 max-w-xl">
                {featured.excerpt}
              </p>
              <span className="inline-block mt-8 text-[#C9A876] text-[11px] tracking-[0.25em] uppercase link-underline">
                Read the guide →
              </span>
            </div>
          </Link>
        </section>

        {/* Remaining articles */}
        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-32 border-t border-[#8C7355]/15 pt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            {rest.map((a, i) => (
              <motion.div
                key={a.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={`/insights/${a.slug}`}
                  data-testid={`insight-card-${a.slug}`}
                  className="group block"
                >
                  <div className="aspect-[16/10] overflow-hidden rounded-sm bg-[#0f1012]">
                    <img
                      src={a.heroImage}
                      alt={a.heroAlt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                    />
                  </div>
                  <div className="mt-5">
                    <ArticleMeta article={a} />
                    <h3 className="font-serif-display italic text-[#F5F4F0] text-3xl mt-3 leading-tight">
                      {a.title}
                    </h3>
                    <p className="text-[#D6D1CA]/65 text-sm mt-2 font-body">{a.subtitle}</p>
                    <p className="text-[#D6D1CA]/60 text-[14px] font-body leading-[1.7] mt-4">
                      {a.excerpt}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
        {/* Downloadable guides */}
        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-24 border-t border-[#8C7355]/15 pt-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase">Studio guides · PDF</p>
              <h2 className="mt-5 font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-5xl">
                One guide per element
              </h2>
            </div>
            <p className="text-[#D6D1CA]/60 font-body max-w-sm text-sm">
              Designed, printable versions of our element guides — free, and yours to send to your
              builder, client or architect.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="guide-download-grid">
            {DOWNLOADS.map((g) => (
              <div key={g.slug} className="border border-[#8C7355]/20 bg-[#1a1b1e]/50 flex flex-col">
                <div className="aspect-[16/10] overflow-hidden bg-[#0f1012]">
                  <img src={g.heroImage} alt={g.heroAlt} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-[#C9A876] text-[9px] tracking-[0.3em] uppercase">{g.element}</span>
                  <h3 className="font-serif-display text-[#F5F4F0] text-xl font-light mt-2 leading-snug">
                    {g.title}
                  </h3>
                  <p className="text-[#D6D1CA]/55 text-xs font-body mt-2 flex-1">{g.subtitle}</p>
                  <button
                    type="button"
                    onClick={() => setGuide(g)}
                    data-testid={`guide-cta-${g.slug}`}
                    className="mt-5 inline-flex items-center gap-2 self-start rounded-full border border-[#8C7355]/50 px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase text-[#C9A876] hover:border-[#C9A876] hover:text-[#dcb884]"
                    style={{ transition: "color .4s ease, border-color .4s ease" }}
                  >
                    <Download size={12} strokeWidth={1.8} />
                    Get the PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <GuideDialog open={Boolean(guide)} onOpenChange={(o) => !o && setGuide(null)} guide={guide} />
      <Footer />
    </div>
  );
}
